import React, { useState, useEffect } from 'react';
import { ChevronDown, FileText, Download, Calendar, Building2, Users, Layers, CheckCircle, Clock, RefreshCw, User } from 'lucide-react';
import { establishmentAPI, employeeAPI, formsAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import Swal from 'sweetalert2';
import { auditActions, getRecentAuditLogs } from '../services/auditService';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const FormsTemplates = ({ onNavigate }) => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State management
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedForm, setSelectedForm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationHistory, setGenerationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoadError, setHistoryLoadError] = useState(false);

  // Generated Forms from database
  const [generatedForms, setGeneratedForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);

  // Available HR/Payroll forms - Categorized by scope

  // INDIVIDUAL FORMS: Generated per employee (5 forms)
  const individualForms = [
    { value: 'form_X', label: 'Form X - Register of Wages', category: 'individual' },
    { value: 'form_q', label: 'Form Q - Appointment Letter', category: 'individual' },
    { value: 'form_xiv', label: 'Form XIV - Employment Card', category: 'individual' },
    { value: 'form_19', label: 'Form 19 - Payslip', category: 'individual' },
    { value: 'form_f', label: 'Form F - Leave Register', category: 'individual' }
  ];

  // CONSOLIDATED FORMS: One form contains all employees (8 forms)
  const consolidatedForms = [
    { value: 'form_a', label: 'Form A - Employee Register', category: 'consolidated' },
    { value: 'form_XXIX', label: 'Form XXIX - Register of Advances and Deductions', category: 'consolidated' },
    { value: 'form_XXIII', label: 'Form XXIII - Wages Register', category: 'consolidated' },
    { value: 'form_v', label: 'Form V - Festival Holidays', category: 'consolidated' },
    { value: 'form_s', label: 'Form S - Notice of Daily Hours of Work', category: 'consolidated' },
    { value: 'form_t', label: 'Form T - Combined Muster Roll cum Register of Wages', category: 'consolidated' },
    { value: 'form_u', label: 'Form U - Combined Annual Return', category: 'consolidated' },
    { value: 'form_XIII', label: 'Form XIII - Minimum Wages Register', category: 'consolidated' }
  ];

  // Additional forms (not part of 11 statutory forms)
  const additionalForms = [
    { value: 'bank_statement', label: 'Bank Statement', category: 'additional' },
    { value: 'esi_statement', label: 'ESI Statement', category: 'additional' },
    { value: 'epf_esi_statement', label: 'EPF ESI Statement', category: 'additional' }
  ];

  // Combine all forms for dropdown
  const forms = [
    { value: 'generate_all', label: '⚡ Generate All Forms (Bulk)', category: 'bulk' },
    ...individualForms,
    ...consolidatedForms,
    ...additionalForms
  ];

  // Month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Year options (current year and previous 2 years)
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (!lockedEstId) return;
    setSelectedEstablishment(lockedEstId);
    setSelectedEmployee('');
    setEmployees([]);
    employeeAPI.getByEstablishment(lockedEstId)
      .then(r => setEmployees(r.employees || []))
      .catch(err => console.error('Error fetching employees:', err));
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch establishments on component mount
  useEffect(() => {
    if (lockedEstId) {
      // ClientAdmin: skip org fetch, load employees for their org directly
      setLoading(true);
      employeeAPI.getByEstablishment(lockedEstId)
        .then(r => setEmployees(r.employees || []))
        .catch(err => console.error('Error fetching employees:', err))
        .finally(() => setLoading(false));
      return;
    }
    const fetchEstablishments = async () => {
      try {
        setLoading(true);
        const response = await establishmentAPI.getAll();
        setEstablishments(response.establishments || []);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        alert('Error loading organizations: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEstablishments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first establishment when establishments are loaded (SuperAdmin only)
  useEffect(() => {
    if (lockedEstId) return;
    if (establishments.length > 0 && !selectedEstablishment) {
      const firstEstId = establishments[0].id;
      setSelectedEstablishment(firstEstId);
      // Fetch employees for first establishment
      const fetchFirstEstEmployees = async () => {
        try {
          setLoading(true);
          const response = await employeeAPI.getByEstablishment(firstEstId);
          setEmployees(response.employees || []);
        } catch (error) {
          console.error('Error fetching employees:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchFirstEstEmployees();
    }
  }, [establishments, selectedEstablishment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch employees when establishment changes
  const handleEstablishmentChange = async (e) => {
    const value = e.target.value;
    setSelectedEstablishment(value);
    setSelectedEmployee('');
    setEmployees([]);
    setFormData(null);

    if (value) {
      try {
        setLoading(true);
        const response = await employeeAPI.getByEstablishment(value);
        setEmployees(response.employees || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
        alert('Error loading employees: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const getMonthLabel = () => {
    const month = months.find(m => m.value === parseInt(selectedMonth));
    return month ? `${month.label} ${selectedYear}` : `${selectedMonth}/${selectedYear}`;
  };

  // Helper function to format timestamp
  const formatTimestamp = (date) => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Fetch generated forms from database
  const fetchGeneratedForms = async () => {
    if (!selectedEstablishment) {
      setGeneratedForms([]);
      return;
    }

    setLoadingForms(true);
    try {
      const params = {
        establishment_id: selectedEstablishment,
        month: selectedMonth,
        year: selectedYear
      };

      if (selectedForm && selectedForm !== 'generate_all') {
        params.form_type = selectedForm;
      }

      console.log('📋 Fetching generated forms with params:', params);
      const response = await formsAPI.listGeneratedForms(params);
      console.log('✅ Generated forms response:', response);

      const formsList = response.forms || [];

      // Filter to show only PDF files (avoid duplicates with Excel)
      const pdfForms = formsList.filter(form =>
        form.file_name && form.file_name.toLowerCase().endsWith('.pdf')
      );

      // Deduplicate - keep only the most recent version of each form
      // Group by: form_type + establishment_id + employee_id (or null) + month + year
      const latestForms = {};
      pdfForms.forEach(form => {
        const key = `${form.form_type}_${form.establishment_id}_${form.employee_id || 'consolidated'}_${form.month}_${form.year}`;

        // Keep the form with the latest generated_at timestamp
        if (!latestForms[key] || new Date(form.generated_at) > new Date(latestForms[key].generated_at)) {
          latestForms[key] = form;
        }
      });

      // Convert back to array and sort by generated_at descending
      const uniqueForms = Object.values(latestForms).sort((a, b) =>
        new Date(b.generated_at) - new Date(a.generated_at)
      );

      setGeneratedForms(uniqueForms);
      console.log(`📊 Loaded ${uniqueForms.length} unique generated forms (filtered from ${pdfForms.length} total)`);
    } catch (error) {
      console.error('❌ Error fetching generated forms:', error);
      setGeneratedForms([]);
    } finally {
      setLoadingForms(false);
    }
  };

  // Load generated forms when filters change
  useEffect(() => {
    if (selectedEstablishment) {
      fetchGeneratedForms();
    }
  }, [selectedEstablishment, selectedForm, selectedMonth, selectedYear]);

  // Load audit trail history on component mount
  useEffect(() => {
    const loadAuditHistory = async () => {
      setLoadingHistory(true);
      setHistoryLoadError(false);
      try {
        // Get recent form generation logs
        const logs = await getRecentAuditLogs(50);

        // Filter only form generation logs and format for display
        const formLogs = logs
          .filter(log => log.resource_type === 'form')
          .map(log => ({
            id: log.id,
            timestamp: new Date(log.timestamp),
            formName: log.resource_name || log.resource_id,
            establishmentName: log.metadata?.establishment_name || 'N/A',
            employeeName: log.metadata?.employee_name || null,
            month: log.metadata?.month,
            year: log.metadata?.year,
            username: log.username,
            isEmployeeForm: !!log.metadata?.employee_name
          }));

        setGenerationHistory(formLogs);
        console.log(`✅ Loaded ${formLogs.length} audit log entries`);
      } catch (error) {
        console.error('❌ Failed to load audit history:', error);
        setHistoryLoadError(true);
      } finally {
        setLoadingHistory(false);
      }
    };

    loadAuditHistory();
  }, []);

  // Helper function to add generation to history (non-blocking)
  const addToHistory = async (data, formType) => {
    try {
      const establishment = establishments.find(e => e.id == selectedEstablishment);
      const employee = employees.find(e => e.employee_id == selectedEmployee);
      const formLabel = forms.find(f => f.value === formType)?.label || formType;

      const targetName = employee
        ? `${employee.first_name} ${employee.last_name}`
        : establishment?.name || 'N/A';

      // Log to audit trail database
      await auditActions.generateForm(
        formType,
        formLabel,
        targetName,
        {
          establishment_id: selectedEstablishment,
          establishment_name: establishment?.name,
          employee_id: selectedEmployee || null,
          employee_name: employee ? `${employee.first_name} ${employee.last_name}` : null,
          month: selectedMonth,
          year: selectedYear,
          file_count: data.generated_count || 1
        }
      );

      // Reload audit history to show the new entry
      const logs = await getRecentAuditLogs(50);
      const formLogs = logs
        .filter(log => log.resource_type === 'form')
        .map(log => ({
          id: log.id,
          timestamp: new Date(log.timestamp),
          formName: log.resource_name || log.resource_id,
          establishmentName: log.metadata?.establishment_name || 'N/A',
          employeeName: log.metadata?.employee_name || null,
          month: log.metadata?.month,
          year: log.metadata?.year,
          username: log.username,
          isEmployeeForm: !!log.metadata?.employee_name
        }));

      setGenerationHistory(formLogs);
    } catch (error) {
      // Log error but don't fail form generation
      console.error('⚠️ Audit logging failed (non-critical):', error);
      console.log('✅ Form generation succeeded, but audit trail unavailable');

      // Add a fallback local entry (not persisted to database)
      const establishment = establishments.find(e => e.id == selectedEstablishment);
      const employee = employees.find(e => e.employee_id == selectedEmployee);
      const formLabel = forms.find(f => f.value === formType)?.label || formType;

      const fallbackEntry = {
        id: Date.now(),
        timestamp: new Date(),
        formName: formLabel,
        establishmentName: establishment?.name || 'N/A',
        employeeName: employee ? `${employee.first_name} ${employee.last_name}` : null,
        month: selectedMonth,
        year: selectedYear,
        username: 'Current User',
        isEmployeeForm: !!employee
      };

      setGenerationHistory(prev => [fallbackEntry, ...prev]);
    }
  };

  const handleGenerateForm = async () => {
    if (!selectedEstablishment) {
      alert('Please select an organization');
      return;
    }

    if (!selectedForm) {
      alert('Please select a form type');
      return;
    }

    const formLabel = forms.find(f => f.value === selectedForm)?.label;
    if (!window.confirm(`Generate ${formLabel} for ${getMonthLabel()}?`)) {
      return;
    }

    setIsGenerating(true);

    try {
      // Build query parameters
      const params = {
        establishment_id: selectedEstablishment,
        month: selectedMonth,
        year: selectedYear
      };

      if (selectedEmployee) {
        params.employee_id = selectedEmployee;
      }

      console.log('📊 Generating form:', selectedForm, params);

      // Check if user selected "Generate All Forms"
      if (selectedForm === 'generate_all') {
        // Call the generate_all_forms endpoint
        const data = await formsAPI.generateAllForms(params);
        setFormData(data);
        console.log('✅ All forms generated successfully:', data);

        // Add to history (MUST await to update table before showing popup)
        await addToHistory(data, 'generate_all');

        // Refresh generated forms list
        await fetchGeneratedForms();

        // Show popup
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'All forms generated successfully',
          confirmButtonText: 'OK',
          confirmButtonColor: '#10b981',
          timer: 3000,
          timerProgressBar: true
        });
      } else {
        // Call API to generate single form using formsAPI with proper Cognito authentication
        const data = await formsAPI.generateForm(selectedForm, params);
        setFormData(data);
        console.log('✅ Form generated successfully:', data);

        // Add to history (MUST await to update table before showing popup)
        await addToHistory(data, selectedForm);

        // Refresh generated forms list
        await fetchGeneratedForms();

        // Show popup
        const formLabel = forms.find(f => f.value === selectedForm)?.label || selectedForm;
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `${formLabel} generated successfully`,
          confirmButtonText: 'OK',
          confirmButtonColor: '#10b981',
          timer: 3000,
          timerProgressBar: true
        });
      }
    } catch (error) {
      console.error('Error generating form:', error);
      alert('Error generating form: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const renderFormData = () => {
    // Don't render formData - we show history table instead
    return null;

    // Handle old bulk generation format (form_results with individual employee files)
    if (formData.form_results && typeof formData.form_results === 'object') {
      const formTypes = Object.keys(formData.form_results);
      const totalSuccess = Object.values(formData.form_results).reduce((sum, form) => sum + (form.generated || 0), 0);
      const totalFailed = Object.values(formData.form_results).reduce((sum, form) => sum + (form.failed || 0), 0);

      return (
        <div>
          <div style={{
            background: '#dcfce7',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            border: '1px solid #86efac'
          }}>
            <div style={{ fontWeight: '600', color: '#166534', marginBottom: '0.25rem' }}>
              ✓ Bulk Generation Complete
            </div>
            <div style={{ fontSize: '0.875rem', color: '#15803d' }}>
              Generated {totalSuccess} files successfully
              {totalFailed > 0 && ` (${totalFailed} failed)`}
            </div>
          </div>

          {/* Form Types Summary */}
          <div style={{
            background: 'var(--theme-surface)',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            overflow: 'hidden'
          }}>
            {formTypes.map((formType, idx) => {
              const formInfo = formData.form_results[formType];
              return (
                <div
                  key={idx}
                  style={{
                    padding: '1rem',
                    borderBottom: idx < formTypes.length - 1 ? '1px solid #e5e7eb' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: '600',
                      color: '#111827',
                      marginBottom: '0.25rem',
                      textTransform: 'uppercase'
                    }}>
                      {formType}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {formInfo.description} - Generated {formInfo.generated} file(s)
                      {formInfo.failed > 0 && ` (${formInfo.failed} failed)`}
                    </div>
                  </div>
                  <span style={{
                    padding: '0.5rem 1rem',
                    background: formInfo.failed > 0 ? '#fef3c7' : '#dcfce7',
                    color: formInfo.failed > 0 ? '#92400e' : '#166534',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    {formInfo.generated} / {formInfo.generated + formInfo.failed}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Note about downloads */}
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: '#eff6ff',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            color: '#1e40af',
            border: '1px solid #bfdbfe'
          }}>
            💡 <strong>Note:</strong> Individual form files can be downloaded from the organization folder or by selecting specific form types from the dropdown.
          </div>

          {/* Summary Info */}
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'var(--theme-surfaceHover)',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>
            📅 Generated for {formData.establishment_name} - {formData.wage_period}
            <br />
            📁 Files saved to: {formData.output_directory}
          </div>
        </div>
      );
    }

    // Handle "Generate All Forms" response (new format with forms array)
    if (formData.forms && Array.isArray(formData.forms)) {
      return (
        <div>
          <div style={{
            background: '#dcfce7',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            border: '1px solid #86efac'
          }}>
            <div style={{ fontWeight: '600', color: '#166534', marginBottom: '0.25rem' }}>
              ✓ All Forms Generated Successfully
            </div>
            <div style={{ fontSize: '0.875rem', color: '#15803d' }}>
              Generated {formData.success_count} of {formData.total_forms} form types ({formData.total_files || 0} total files)
              {formData.failed_count > 0 && ` - ${formData.failed_count} failed`}
            </div>
          </div>

          {/* Master ZIP Download */}
          {formData.download_url && formData.master_zip_file && (
            <div style={{
              background: 'var(--theme-surface)',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.5rem', fontSize: '1.125rem' }}>
                  📦 {formData.master_zip_file}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  {formData.establishment_name} - {months.find(m => m.value === formData.month)?.label} {formData.year}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  Contains all {formData.total_files} generated files
                </div>
              </div>
              <a
                href={formData.download_url}
                download
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: '#3b82f6',
                  color: 'white',
                  borderRadius: '0.375rem',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'}
                onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}
              >
                <Download size={20} />
                Download All Forms (ZIP)
              </a>
            </div>
          )}

          {/* Individual Form Results */}
          <div style={{
            marginBottom: '1rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#374151'
          }}>
            Form Generation Details:
          </div>

          {/* Forms List */}
          <div style={{
            background: 'var(--theme-surface)',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            overflow: 'hidden'
          }}>
            {formData.forms.map((form, idx) => (
              <div
                key={idx}
                style={{
                  padding: '1rem',
                  borderBottom: idx < formData.forms.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: form.status === 'failed' ? '#fef2f2' : '#ffffff'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: '600',
                    color: form.status === 'failed' ? '#dc2626' : '#111827',
                    marginBottom: '0.25rem',
                    textTransform: 'uppercase'
                  }}>
                    {form.form_type}
                  </div>
                  {form.status === 'success' ? (
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      Generated {form.file_count || 1} file(s)
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                      Error: {form.error}
                    </div>
                  )}
                </div>
                {form.status === 'success' ? (
                  <span style={{
                    padding: '0.5rem 1rem',
                    background: '#dcfce7',
                    color: '#166534',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    ✓ Success
                  </span>
                ) : (
                  <span style={{
                    padding: '0.5rem 1rem',
                    background: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    ✗ Failed
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Summary Info */}
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'var(--theme-surfaceHover)',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>
            📅 Generated for {formData.establishment_name || `Establishment ${formData.establishment_id}`} - {months.find(m => m.value === formData.month)?.label} {formData.year}
            {formData.s3_uri && (
              <>
                <br />
                ☁️ Stored in S3: {formData.s3_key}
                <div style={{ marginTop: '0.25rem', fontSize: '0.7rem' }}>
                  Download link expires in 1 hour
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    // Check if formData is an array or object
    if (Array.isArray(formData)) {
      // Display as table
      if (formData.length === 0) return <p style={{ color: '#6b7280' }}>No data available</p>;

      const headers = Object.keys(formData[0]);

      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
            background: 'var(--theme-surface)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '0.5rem'
          }}>
            <thead>
              <tr style={{ background: 'var(--theme-surfaceHover)', borderBottom: '2px solid #e5e7eb' }}>
                {headers.map(header => (
                  <th key={header} style={{
                    padding: '1rem',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {header.replace(/_/g, ' ').toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formData.map((row, index) => (
                <tr
                  key={index}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f1f3f4'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                >
                  {headers.map(header => (
                    <td key={header} style={{ padding: '1rem', color: '#374151' }}>
                      {row[header] !== null && row[header] !== undefined ? String(row[header]) : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else if (formData.generated_files && Array.isArray(formData.generated_files)) {
      // Display generated files with download links
      return (
        <div>
          <div style={{
            background: '#dcfce7',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            border: '1px solid #86efac'
          }}>
            <div style={{ fontWeight: '600', color: '#166534', marginBottom: '0.25rem' }}>
              ✓ {formData.generated_count || formData.generated_files.length} form(s) generated successfully
            </div>
            <div style={{ fontSize: '0.875rem', color: '#15803d' }}>
              {formData.validation_summary?.all_valid ? 'All forms validated' : 'Forms generated'}
            </div>
          </div>

          {/* ZIP Download Button - if available */}
          {formData.zip_download_url && (
            <div style={{
              background: 'var(--theme-surface)',
              border: '2px solid #3b82f6',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.5rem', fontSize: '1.125rem' }}>
                  📦 {formData.form_type?.toUpperCase()} - All Files
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  {formData.establishment_name} - {months.find(m => m.value === formData.month)?.label} {formData.year}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  Contains {formData.generated_count} PDF files
                </div>
                {formData.s3_uri && (
                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                    ☁️ Stored in S3: {formData.s3_key}
                    <div style={{ marginTop: '0.25rem' }}>
                      Download link expires in 1 hour
                    </div>
                  </div>
                )}
              </div>
              <a
                href={formData.zip_download_url}
                download
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: '#3b82f6',
                  color: 'white',
                  borderRadius: '0.375rem',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'}
                onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}
              >
                <Download size={20} />
                Download ZIP
              </a>
            </div>
          )}

          {/* Files List - Only show if NO ZIP download (single employee generation) */}
          {!formData.zip_download_url && (
            <div style={{
              background: 'var(--theme-surface)',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              overflow: 'hidden'
            }}>
              {formData.generated_files.map((file, idx) => (
              <div
                key={idx}
                style={{
                  padding: '1rem',
                  borderBottom: idx < formData.generated_files.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                    {file.employee_name || file.file_name}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {file.designation && `${file.designation} • `}
                    {file.file_name}
                  </div>
                </div>
                <a
                  href={file.download_url || `${process.env.REACT_APP_API_BASE_URL || ''}/api/forms/download/${file.file_name}`}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: '#3b82f6',
                    color: 'white',
                    borderRadius: '0.375rem',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}
                >
                  <Download size={16} />
                  Download
                </a>
              </div>
            ))}
          </div>
          )}

          {/* Output Directory */}
          {formData.output_directory && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'var(--theme-surfaceHover)',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              color: '#6b7280',
              fontFamily: 'monospace'
            }}>
              📁 {formData.output_directory}
            </div>
          )}
        </div>
      );
    } else if (formData.success && formData.file_name && formData.download_url) {
      // Single file generation response
      return (
        <div>
          <div style={{
            background: '#dcfce7',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            border: '1px solid #86efac'
          }}>
            <div style={{ fontWeight: '600', color: '#166534', marginBottom: '0.25rem' }}>
              ✓ Form generated successfully
            </div>
            <div style={{ fontSize: '0.875rem', color: '#15803d' }}>
              {formData.message || 'Form is ready for download'}
            </div>
          </div>

          <div style={{
            background: 'var(--theme-surface)',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                {formData.file_name}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {formData.form_type?.toUpperCase()} - {formData.establishment_name || `Establishment ${formData.establishment_id}`}
              </div>
            </div>
            <a
              href={formData.download_url}
              download
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#3b82f6',
                color: 'white',
                borderRadius: '0.375rem',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'}
              onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}
            >
              <Download size={16} />
              Download
            </a>
          </div>

          {/* S3 Info */}
          {formData.s3_uri && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'var(--theme-surfaceHover)',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              color: '#6b7280',
              fontFamily: 'monospace'
            }}>
              ☁️ Stored in S3: {formData.s3_key}
              {formData.expires_in && (
                <div style={{ marginTop: '0.25rem', fontSize: '0.7rem' }}>
                  Download link expires in {Math.floor(formData.expires_in / 3600)} hour(s)
                </div>
              )}
            </div>
          )}
        </div>
      );
    } else if (formData.zip_download_url) {
      // Bulk ZIP file response
      return (
        <div>
          <div style={{
            background: '#dcfce7',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            border: '1px solid #86efac'
          }}>
            <div style={{ fontWeight: '600', color: '#166534', marginBottom: '0.25rem' }}>
              ✓ {formData.generated_count} form(s) generated successfully
            </div>
            <div style={{ fontSize: '0.875rem', color: '#15803d' }}>
              {formData.message || 'Forms packaged in ZIP file'}
            </div>
          </div>

          <div style={{
            background: 'var(--theme-surface)',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                📦 {formData.zip_file ? formData.zip_file.split('/').pop() : 'Bulk Forms Package'}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {formData.establishment_name} - {formData.wage_period}
              </div>
            </div>
            <a
              href={formData.zip_download_url}
              download
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: '#3b82f6',
                color: 'white',
                borderRadius: '0.375rem',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'}
              onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}
            >
              <Download size={20} />
              Download ZIP
            </a>
          </div>

          {/* S3 Info */}
          {formData.s3_uri && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'var(--theme-surfaceHover)',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              color: '#6b7280',
              fontFamily: 'monospace'
            }}>
              ☁️ Stored in S3: {formData.s3_key}
              {formData.expires_in && (
                <div style={{ marginTop: '0.25rem', fontSize: '0.7rem' }}>
                  Download link expires in {Math.floor(formData.expires_in / 3600)} hour(s)
                </div>
              )}
            </div>
          )}
        </div>
      );
    } else {
      // Fallback: Display as key-value pairs (for other types of responses)
      return (
        <div style={{
          background: 'var(--theme-surfaceHover)',
          padding: '1.5rem',
          borderRadius: '0.5rem',
          border: '1px solid #e5e7eb'
        }}>
          {Object.entries(formData).map(([key, value]) => (
            <div
              key={key}
              style={{
                display: 'flex',
                padding: '0.75rem 0',
                borderBottom: '1px solid #e5e7eb',
                ':last-child': { borderBottom: 'none' }
              }}
            >
              <div style={{
                flex: '0 0 30%',
                fontWeight: '600',
                color: '#111827',
                textTransform: 'capitalize'
              }}>
                {key.replace(/_/g, ' ')}:
              </div>
              <div style={{ flex: 1, color: '#374151' }}>
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        <div className="update-employee-header">
          <h2 className="update-employee-title">Statutory Forms Generator</h2>
          <p className="update-employee-subtitle">Generate statutory HR and Payroll forms for employees</p>
        </div>

        {/* Selection Filters */}
        <div className="attendance-grid-filters">
          {/* Organization Dropdown — hidden for ClientAdmin */}
          {!lockedEstId && (
          <div className="attendance-filter-group">
            <label className="attendance-filter-label">
              Organization
            </label>
            <select
              value={selectedEstablishment}
              onChange={handleEstablishmentChange}
              className="attendance-filter-select"
              disabled={loading}
            >
              <option value="">
                {loading ? 'Loading...' : 'Select Organization'}
              </option>
              {establishments.map((est) => (
                <option key={est.id} value={est.id}>
                  {est.name}
                </option>
              ))}
            </select>
          </div>
          )}

          {/* Employee Dropdown (Optional) */}
          {selectedEstablishment && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper employee">
                  <Users size={15} />
                </span>
                Employee {consolidatedForms.some(f => f.value === selectedForm) ? '(All)' : '(Optional)'}
              </label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="attendance-filter-select"
                disabled={loading || consolidatedForms.some(f => f.value === selectedForm)}
                style={consolidatedForms.some(f => f.value === selectedForm) ? { backgroundColor: 'var(--theme-surfaceHover)', cursor: 'not-allowed', color: '#9ca3af' } : {}}
              >
                <option value="">All Employees</option>
                {!consolidatedForms.some(f => f.value === selectedForm) && employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_id}) - {emp.department || 'N/A'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Form Type Dropdown */}
          {selectedEstablishment && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper type">
                  <Layers size={15} />
                </span>
                Form Type
              </label>
              <select
                value={selectedForm}
                onChange={(e) => setSelectedForm(e.target.value)}
                className="attendance-filter-select"
              >
                <option value="">Select Form Type</option>
                {forms.map(form => (
                  <option key={form.value} value={form.value}>
                    {form.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Month Dropdown */}
          {selectedEstablishment && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper month">
                  <Calendar size={15} />
                </span>
                Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="attendance-filter-select"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Year Dropdown */}
          {selectedEstablishment && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper month">
                  <Calendar size={15} />
                </span>
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="attendance-filter-select"
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Data Display Section */}
        {selectedEstablishment && (
          <div className="update-employee-form animate-fadeIn" style={{ marginTop: '1.5rem' }}>
            <div className="update-employee-content">
              {/* Header with Action Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                padding: '1rem',
                background: 'var(--theme-surfaceHover)',
                borderRadius: '0.5rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={20} style={{ color: '#059669' }} />
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                    {selectedForm ? `${forms.find(f => f.value === selectedForm)?.label} - ${getMonthLabel()}` : `Forms for ${getMonthLabel()}`}
                  </h3>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={handleGenerateForm}
                    disabled={isGenerating || !selectedEstablishment || !selectedForm}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: (isGenerating || !selectedForm) ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: (isGenerating || !selectedForm) ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      opacity: (isGenerating || !selectedForm) ? 0.6 : 1
                    }}
                  >
                    {isGenerating ? (
                      <>
                        <div className="update-employee-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText size={16} />
                        Generate Form
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {dataLoading && (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                  <div className="update-employee-spinner" style={{ margin: '0 auto 1rem' }}></div>
                  <p>Loading form data...</p>
                </div>
              )}

              {/* No Data State */}
              {!dataLoading && !isGenerating && !formData && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#6b7280',
                  background: 'var(--theme-surfaceHover)',
                  borderRadius: '0.5rem',
                  border: '2px dashed #d1d5db'
                }}>
                  <FileText size={48} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    No form data generated yet
                  </p>
                  <p style={{ fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                    Select a form type and click "Generate Form" to create the selected HR/Payroll form.
                  </p>
                  <div style={{
                    display: 'inline-block',
                    padding: '1rem',
                    background: '#eff6ff',
                    borderRadius: '0.5rem',
                    border: '1px solid #bfdbfe'
                  }}>
                    <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0, textAlign: 'left' }}>
                      <strong>💡 Tip:</strong> Select from {forms.length} available forms:
                    </p>
                    <ul style={{ fontSize: '0.875rem', color: '#1e40af', margin: '0.5rem 0 0 1.5rem', textAlign: 'left' }}>
                      <li>Muster Roll, Wage Register</li>
                      <li>Employee Cards, Payslips, Banking Details</li>
                      <li>Annual Returns, and more</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Generating State */}
              {isGenerating && (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                  <div className="update-employee-spinner" style={{ margin: '0 auto 1.5rem', width: '48px', height: '48px' }}></div>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Generating Form...
                  </p>
                  <p style={{ fontSize: '0.875rem' }}>
                    Please wait while we generate your {selectedForm ? forms.find(f => f.value === selectedForm)?.label : 'form'}.
                  </p>
                </div>
              )}

              {/* Informational Banner - Only show when there are entries */}
              {generatedForms.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: '1px solid #059669',
                  borderRadius: '0.75rem',
                  padding: '1rem 1.25rem',
                  marginBottom: '1.5rem',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}>
                  <CheckCircle size={24} style={{ flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
                      {generatedForms.length} Forms Available
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.95 }}>
                      Download links are available below. Forms are stored in S3 and accessible anytime.
                    </div>
                  </div>
                </div>
              )}

              {/* Generated Forms Table with Download Links */}
              <div style={{
                background: 'var(--theme-surface)',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  background: 'var(--theme-surfaceHover)',
                  borderBottom: '2px solid #e5e7eb',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Download size={20} style={{ color: '#6b7280' }} />
                    <h3 style={{
                      margin: 0,
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#111827'
                    }}>
                      Generated Forms - {getMonthLabel()}
                    </h3>
                    {generatedForms.length > 0 && (
                      <span style={{
                        background: '#10b981',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {generatedForms.length} {generatedForms.length === 1 ? 'form' : 'forms'}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={fetchGeneratedForms}
                    disabled={loadingForms}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: '#3b82f6',
                      border: 'none',
                      borderRadius: '0.375rem',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: loadingForms ? 'not-allowed' : 'pointer',
                      opacity: loadingForms ? 0.6 : 1
                    }}
                  >
                    <RefreshCw size={16} style={{ animation: loadingForms ? 'spin 1s linear infinite' : 'none' }} />
                    Refresh
                  </button>
                </div>

                {loadingForms ? (
                  <div style={{
                    padding: '3rem 2rem',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <div className="update-employee-spinner" style={{ margin: '0 auto 1rem' }}></div>
                    <p style={{ fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                      Loading forms...
                    </p>
                  </div>
                ) : generatedForms.length === 0 ? (
                  <div style={{
                    padding: '3rem 2rem',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <FileText size={48} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
                    <p style={{ fontSize: '1rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.5rem' }}>
                      No forms generated yet
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                      Generate forms for {getMonthLabel()} to see them here
                    </p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--theme-surfaceHover)', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Form Name
                        </th>
                        <th style={{
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Employee
                        </th>
                        <th style={{
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Generated At
                        </th>
                        <th style={{
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Download
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedForms.map((form, index) => (
                        <tr key={form.id || index} style={{
                          borderBottom: index < generatedForms.length - 1 ? '1px solid #e5e7eb' : 'none',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f4'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{
                            padding: '1rem',
                            fontSize: '0.875rem',
                            color: '#111827',
                            fontWeight: '500'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <FileText size={16} style={{ color: '#6b7280' }} />
                              {form.form_name || form.form_type}
                            </div>
                          </td>
                          <td style={{
                            padding: '1rem',
                            fontSize: '0.875rem',
                            color: '#374151'
                          }}>
                            {form.employee_name ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <User size={14} style={{ color: '#6b7280' }} />
                                {form.employee_name}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>All Employees</span>
                            )}
                          </td>
                          <td style={{
                            padding: '1rem',
                            fontSize: '0.875rem',
                            color: '#6b7280'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Clock size={14} style={{ color: '#9ca3af' }} />
                              {new Date(form.generated_at).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                              {form.s3_url || form.pdf_url ? (
                                <a
                                  href={form.s3_url || form.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1rem',
                                    background: 'transparent',
                                    color: '#2a4d8f',
                                    borderRadius: '0.375rem',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    border: '1.5px solid #2a4d8f',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Download size={16} />
                                  Download
                                </a>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                                  Not available
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Generation Activity Log - Always visible */}
              <div style={{
                background: 'var(--theme-surface)',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: 'var(--theme-surfaceHover)',
                  borderBottom: '2px solid #e5e7eb',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <FileText size={20} style={{ color: '#6b7280' }} />
                  <h3 style={{
                    margin: 0,
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    Generation Activity Log
                  </h3>
                  {generationHistory.length > 0 && (
                    <span style={{
                      marginLeft: 'auto',
                      background: '#3b82f6',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '1rem',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {generationHistory.length} {generationHistory.length === 1 ? 'operation' : 'operations'}
                    </span>
                  )}
                </div>

                {generationHistory.length === 0 ? (
                  // Empty state
                  <div style={{
                    padding: '3rem 2rem',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <FileText size={48} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
                    <p style={{ fontSize: '1rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.5rem' }}>
                      No forms generated yet
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                      Generate forms to see activity history here
                    </p>
                  </div>
                ) : (
                  // Table with data
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--theme-surfaceHover)', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Date & Time
                        </th>
                        <th style={{
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Form Name
                        </th>
                        <th style={{
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Generated For
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {generationHistory.map((entry, index) => (
                        <tr key={entry.id} style={{
                          borderBottom: index < generationHistory.length - 1 ? '1px solid #e5e7eb' : 'none',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f4'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{
                            padding: '1rem',
                            fontSize: '0.875rem',
                            color: '#6b7280'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Clock size={14} style={{ color: '#9ca3af' }} />
                              {formatTimestamp(entry.timestamp)}
                            </div>
                          </td>
                          <td style={{
                            padding: '1rem',
                            fontSize: '0.875rem',
                            color: '#111827',
                            fontWeight: '500'
                          }}>
                            {entry.formName}
                          </td>
                          <td style={{
                            padding: '1rem',
                            fontSize: '0.875rem',
                            color: '#374151'
                          }}>
                            {entry.employeeName ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Users size={14} style={{ color: '#6b7280' }} />
                                {entry.employeeName}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Building2 size={14} style={{ color: '#6b7280' }} />
                                {entry.establishmentName}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormsTemplates;
